import { LexoRank } from 'lexorank';
import dayjs, { Dayjs } from 'dayjs';

import { CreateGql, UpdateGql, DeleteGql } from './gql';

type Data = {
  id?: string;
  order: string | LexoRank;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
};

type DataToEdit = {
  name?: string;
};

type DataToSave = {
  order: string;
  name: string;
};

class OriginalIngredientCategory {

  readonly id?: string;
  readonly order: LexoRank;
  readonly name: string;
  readonly createdAt?: Dayjs;
  readonly updatedAt?: Dayjs;

  private readonly createGql: CreateGql;
  private readonly updateGql: UpdateGql;
  private readonly deleteGql: DeleteGql;

  constructor(data: Data) {
    this.id = data.id;
    this.order = data.order instanceof LexoRank ? data.order : LexoRank.parse(data.order);
    this.name = data.name ?? '';
    this.createdAt = data.createdAt ? dayjs(data.createdAt) : undefined;
    this.updatedAt = data.updatedAt ? dayjs(data.updatedAt) : undefined;
    this.createGql = new CreateGql();
    this.updateGql = new UpdateGql();
    this.deleteGql = new DeleteGql();
  }

  edit(data: DataToEdit): this {
    return Object.assign(Object.create(this.constructor.prototype), { ...this, ...data });
  }

  validate(): boolean {
    if (!this.name) return false;
    return true;
  }

  async save(): Promise<OriginalIngredientCategory> {
    if (!this.validate()) throw new Error('invalid cache object');
    return this.id ? this.update() : this.create();
  }

  private async create(): Promise<OriginalIngredientCategory> {
    const createGql = await this.createGql.fetch({ input: this.getDataToSave() });
    if (!createGql.document) throw new Error('invalid document');
    return new OriginalIngredientCategory(createGql.document);
  }

  private async update(): Promise<OriginalIngredientCategory> {
    if (!this.id) throw new Error('invalid relationship');
    const updateGql = await this.updateGql.fetch({ id: this.id, input: this.getDataToSave() });
    if (!updateGql.document) throw new Error('invalid document');
    return new OriginalIngredientCategory(updateGql.document);
  }

  private getDataToSave(): DataToSave {
    const { order, name } = this;
    return { order: order.toString(), name };
  }

  async delete(): Promise<OriginalIngredientCategory> {
    if (!this.id) throw new Error('invalid relationship');
    const deleteGql = await this.deleteGql.fetch({ id: this.id });
    if (!deleteGql.document) throw new Error('invalid document');
    return new OriginalIngredientCategory(deleteGql.document);
  }

  toJSON(): Data {
    const { id, name } = this;
    const order = this.order.toString();
    const createdAt = this.createdAt?.toJSON();
    const updatedAt = this.updatedAt?.toJSON();
    return { id, order, name, createdAt, updatedAt };
  }

  static sort(categories: OriginalIngredientCategory[]) {
    function orderByOrder(a: OriginalIngredientCategory, b: OriginalIngredientCategory) {
      if (a.order.toString() < b.order.toString()) return -1;
      else if (a.order.toString() > b.order.toString()) return 1;
      else return 0;
    }
    return categories.sort(orderByOrder);
  }

  static buildNext(list: OriginalIngredientCategory[]): OriginalIngredientCategory {
    const order = list[list.length - 1]?.order.genNext() ?? LexoRank.middle();
    return new OriginalIngredientCategory({ order });
  }

}

export { OriginalIngredientCategory };
export type {
  Data as OriginalIngredientCategoryData,
  DataToSave as OriginalIngredientCategoryDataToSave,
};

export * from './collection';